import { logger } from '@/src/internal/logger';

import { HttpClientError, type HttpError, HttpNetworkError } from '../errors';
import { type HttpStatusCode } from '../types';

import { type ClientResponseOrError } from './HttpClient';

/**
 * Defines the retry strategy to use for HTTP requests.
 * Exponential backoff increases the delay with each retry attempt,
 * while fixed delay uses the same delay for each attempt.
 */
export enum RetryStrategy {
  Exponential = 'exponential',
  Fixed = 'fixed',
}

type ClientRetryPromise<T> = () => Promise<ClientResponseOrError<T>>;

type HttpRetryRecord = {
  readonly error: HttpError;
};

export interface HttpRequestWithRetryOptions {
  /**
   * Base delay in milliseconds between retries.
   * For exponential strategy, this is multiplied by 2^attemptNumber.
   *
   * @default 1000ms
   */
  readonly delayMs?: number;

  /**
   * Whether to add jitter to exponential backoff.
   * Jitter helps prevent thundering herd problem by randomizing delays
   * Adds a random amount of time (0-25% of delay) to each retry delay.
   *
   * @default false
   */
  readonly jitter?: boolean;

  /**
   * Maximum delay in milliseconds. Prevents exponential backoff from growing too large.
   *
   * @default 30000ms
   */
  readonly maxDelayMs?: number;

  /**
   * Maximum number of retry attempts.
   *
   * @default 3
   */
  readonly maxRetries?: number;

  /**
   * HTTP status codes that should trigger a retry.
   *
   * @default [408, 409, 429, 500, 502, 503, 504]
   */
  readonly retryableStatusCodes?: HttpStatusCode[];

  /**
   * Whether to retry on network errors.
   *
   * @default true
   */
  readonly retryOnNetworkError?: boolean;

  /**
   * Enable retries on errors. Set to false to disable retries and fail immediately on error.
   *
   * @default true
   */
  readonly shouldRetryOnError?: boolean;

  /**
   * Retry strategy to use. Default: Fixed
   */
  readonly strategy?: RetryStrategy;
}

const DEFAULT_RETRY_CONFIG: Required<HttpRequestWithRetryOptions> = {
  delayMs: 1000,
  jitter: false,
  maxDelayMs: 30000,
  maxRetries: 3,
  retryableStatusCodes: [408, 409, 429, 500, 502, 503, 504],
  retryOnNetworkError: true,
  shouldRetryOnError: true,
  strategy: RetryStrategy.Fixed,
};

const establishOptions = (
  overrides?: HttpRequestWithRetryOptions,
): Required<HttpRequestWithRetryOptions> => ({
  delayMs: overrides?.delayMs ?? DEFAULT_RETRY_CONFIG.delayMs,
  jitter: overrides?.jitter ?? DEFAULT_RETRY_CONFIG.jitter,
  maxDelayMs: overrides?.maxDelayMs ?? DEFAULT_RETRY_CONFIG.maxDelayMs,
  maxRetries: overrides?.maxRetries ?? DEFAULT_RETRY_CONFIG.maxRetries,
  retryableStatusCodes:
    overrides?.retryableStatusCodes ?? DEFAULT_RETRY_CONFIG.retryableStatusCodes,
  retryOnNetworkError: overrides?.retryOnNetworkError ?? DEFAULT_RETRY_CONFIG.retryOnNetworkError,
  shouldRetryOnError: overrides?.shouldRetryOnError ?? DEFAULT_RETRY_CONFIG.shouldRetryOnError,
  strategy: overrides?.strategy ?? DEFAULT_RETRY_CONFIG.strategy,
});

const shouldRetry = (history: HttpRetryRecord[], options: HttpRequestWithRetryOptions): boolean => {
  if (history.length === 0) {
    return true;
  } else if (options.shouldRetryOnError === false) {
    return false;
  } else if (history.length > (options.maxRetries ?? DEFAULT_RETRY_CONFIG.maxRetries)) {
    return false;
  }
  const lastError = history[history.length - 1];
  const isNetworkError = lastError.error instanceof HttpNetworkError;
  if ((options.retryOnNetworkError ?? DEFAULT_RETRY_CONFIG.retryOnNetworkError) && isNetworkError) {
    return true;
  }

  if (lastError.error instanceof HttpClientError) {
    const status = lastError.error.status;
    return !!options.retryableStatusCodes?.includes(status);
  }
  return false;
};

const createDelay = async (
  config: HttpRequestWithRetryOptions,
  attemptNumber: number,
): Promise<void> => {
  let delay: number;
  if (config.strategy === RetryStrategy.Exponential) {
    delay = (config.delayMs ?? DEFAULT_RETRY_CONFIG.delayMs) * 2 ** attemptNumber;
  } else {
    delay = config.delayMs ?? DEFAULT_RETRY_CONFIG.delayMs;
  }

  if (config.jitter) {
    const jitter = Math.random() * delay * 0.25;
    delay += jitter;
  }

  delay = Math.min(delay, config.maxDelayMs ?? DEFAULT_RETRY_CONFIG.maxDelayMs);
  return new Promise(resolve => setTimeout(resolve, delay));
};

/**
 * Utility function to perform HTTP requests with automatic retries based on configurable options.
 * The function takes a promise factory that executes the HTTP request and returns a
 * response or error. It will retry the request based on the provided options, such as
 * retry strategy, delay, and which errors to retry on.
 *
 * @param {ClientRetryPromise} promiseFactory A promise that typically wraps a call to
 *   the HttpClient.
 * @param {HttpRequestWithRetryOptions} overrides Optional configuration to override
 *   default retry behavior.
 */
export const httpRequestWithRetries = async <T>(
  promiseFactory: ClientRetryPromise<T>,
  overrides?: HttpRequestWithRetryOptions,
): Promise<ClientResponseOrError<T>> => {
  const config = establishOptions(overrides);
  const history: HttpRetryRecord[] = [];

  while (shouldRetry(history, config)) {
    /* eslint-disable-next-line no-await-in-loop -- These requests have to be executed
         sequentially. */
    const { error, meta, response } = await promiseFactory();
    if (!error) {
      return { meta, response };
    }
    history.push({ error });
    if (history.length === 0) {
      logger.error(
        { error, history, meta, response },
        'httpRequestWithRetries is in an invalid state. ' +
          'retry history is empty after a failed attempt',
      );
      throw new Error('httpRequestWithRetries is in an invalid state');
    }
    /* eslint-disable-next-line no-await-in-loop -- We need to wait for the delay before
       making the next retry attempt.  */
    await createDelay(config, history.length - 1);
  }
  return { error: history[history.length - 1].error };
};
